import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import BaseModal from 'components/Modals/BaseModal';
import Button from 'components/Button';
import TextField from 'components/Forms/TextField';
import LoadingSpinner from 'components/LoadingSpinner';
import cx from 'classnames';
import '../ImageModal.sass';

const ALL_FOLDER_VALUE = '';

const formatFileSize = (bytes = 0) => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';

  return new Date(timestamp).toLocaleString();
};

const splitFileName = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex <= 0) {
    return { name: fileName, extension: '' };
  }

  return {
    name: fileName.substring(0, lastDotIndex),
    extension: fileName.substring(lastDotIndex + 1),
  };
};

function EditImageModal({
  isOpen,
  asset,
  folders,
  onClose,
  onSave,
  loading,
  isLoading,
}) {
  const intl = useIntl();
  const folderMenuRef = useRef(null);

  const [imageName, setImageName] = useState('');
  const [imageExtension, setImageExtension] = useState('');
  const [folderId, setFolderId] = useState(ALL_FOLDER_VALUE);
  const [isFolderMenuOpen, setIsFolderMenuOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const folderSearchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && asset) {
      const { name, extension } = splitFileName(
        asset.originalName || asset.savedName || '',
      );
      setImageName(name);
      setImageExtension(extension);
      setFolderId(asset.folderId || ALL_FOLDER_VALUE);
      setIsFolderMenuOpen(false);
    }
  }, [isOpen, asset]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        folderMenuRef.current &&
        !folderMenuRef.current.contains(event.target)
      ) {
        setIsFolderMenuOpen(false);
      }
    };

    if (isFolderMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (folderSearchInputRef.current) {
        folderSearchInputRef.current.focus();
      }
    } else {
      setFolderSearch('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFolderMenuOpen]);

  if (!asset) {
    return null;
  }

  if (isLoading) {
    return (
      <BaseModal
        isOpen={isOpen}
        headerTitle={
          asset.type === 'icon'
            ? 'image-modal.edit-image.title-icon'
            : 'image-modal.edit-image.title'
        }
        maxWidth="600px"
        onClose={onClose}>
        <div className="image-modal__edit-image image-modal__edit-image--loading">
          <LoadingSpinner />
        </div>
      </BaseModal>
    );
  }

  const { metadata, createdAt, thumbUrl } = asset;

  const assetFolderId = asset.folderId || ALL_FOLDER_VALUE;
  const hasSelectedFolder = Boolean(folderId);

  const fullOriginalName = imageExtension
    ? `${imageName.trim()}.${imageExtension}`
    : imageName.trim();

  const canEditName = true;
  const nameChanged =
    fullOriginalName !== (asset.originalName || asset.savedName || '').trim();
  const folderChanged = folderId !== assetFolderId;
  const hasChanges = nameChanged || folderChanged;

  const handleFolderSelect = (selectedFolderId) => {
    setFolderId(selectedFolderId);
    setIsFolderMenuOpen(false);
  };

  const handleSave = () => {
    const newOriginalName = imageExtension
      ? `${imageName.trim()}.${imageExtension}`
      : imageName.trim();

    onSave({
      originalName: newOriginalName,
      folderId: hasSelectedFolder ? folderId : undefined,
    });
  };

  const selectedFolderName =
    folders.find((folder) => folder.id === folderId)?.name ||
    intl.formatMessage({ id: 'image-modal.global-images.folder-all' });

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearch.toLowerCase()),
  );

  const showAllOption = !hasSelectedFolder;

  return (
    <BaseModal
      isOpen={isOpen}
      headerTitle={
        asset.type === 'icon'
          ? 'image-modal.edit-image.title-icon'
          : 'image-modal.edit-image.title'
      }
      maxWidth="600px"
      onClose={onClose}>
      <div className="image-modal__edit-image">
        <div className="image-modal__edit-image__top">
          <div className="image-modal__edit-image__preview">
            <img src={thumbUrl} alt={fullOriginalName} />
          </div>
          <div className="image-modal__edit-image__form">
            {canEditName && (
              <TextField
                label={intl.formatMessage({
                  id:
                    asset.type === 'icon'
                      ? 'image-modal.edit-image.name-label-icon'
                      : 'image-modal.edit-image.name-label',
                })}
                value={imageName}
                onChange={(e) => setImageName(e.target.value)}
                fullWidth
              />
            )}
            <div
              className="image-modal__edit-image__folder"
              ref={folderMenuRef}>
              <span className="image-modal__edit-image__folder-label">
                {intl.formatMessage({
                  id: 'image-modal.edit-image.folder-label',
                })}
              </span>
              <button
                type="button"
                className="image-modal__edit-image__folder-dropdown"
                onClick={() => setIsFolderMenuOpen((prev) => !prev)}>
                <span
                  className="image-modal__edit-image__folder-value"
                  title={selectedFolderName}>
                  {selectedFolderName}
                </span>
                <span className="icon icon-expand-more" />
              </button>
              {isFolderMenuOpen && (
                <div className="image-modal__edit-image__folder-menu">
                  <div className="image-modal__edit-image__folder-menu-search">
                    <input
                      ref={folderSearchInputRef}
                      type="search"
                      className="image-modal__edit-image__folder-menu-search__input"
                      placeholder={intl.formatMessage({
                        id: 'image-modal.global-images.folder-search-placeholder',
                      })}
                      value={folderSearch}
                      onChange={(e) => setFolderSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {showAllOption && (
                    <button
                      type="button"
                      className={cx(
                        'image-modal__edit-image__folder-menu-item',
                        {
                          'image-modal__edit-image__folder-menu-item--selected':
                            !folderId,
                        },
                      )}
                      onClick={() => handleFolderSelect(ALL_FOLDER_VALUE)}>
                      {intl.formatMessage({
                        id: 'image-modal.global-images.folder-all',
                      })}{' '}
                      <span className="image-modal__edit-image__folder-menu-item__default-folder">
                        {intl.formatMessage({
                          id: 'image-modal.global-images.default-folder',
                        })}
                      </span>
                    </button>
                  )}
                  {filteredFolders.map((folder) => (
                    <button
                      key={folder.id}
                      type="button"
                      className={cx(
                        'image-modal__edit-image__folder-menu-item',
                        {
                          'image-modal__edit-image__folder-menu-item--selected':
                            folder.id === folderId,
                        },
                      )}
                      onClick={() => handleFolderSelect(folder.id)}>
                      {folder.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="image-modal__edit-image__metadata">
          <div className="image-modal__edit-image__metadata-item">
            <FormattedMessage id="image-modal.edit-image.dimensions" />
            {`: ${metadata?.width || 0}x${metadata?.height || 0} PX`}
          </div>
          <div className="image-modal__edit-image__metadata-item">
            <FormattedMessage id="image-modal.edit-image.format" />
            {`: ${metadata?.format || '-'}`}
          </div>
          <div className="image-modal__edit-image__metadata-item">
            <FormattedMessage id="image-modal.edit-image.size" />
            {`: ${formatFileSize(metadata?.size)}`}
          </div>
          <div className="image-modal__edit-image__metadata-item">
            <FormattedMessage id="image-modal.edit-image.date-added" />
            {`: ${formatDate(createdAt)}`}
          </div>
        </div>
        <div className="image-modal__edit-image__actions">
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            <FormattedMessage id="image-modal.edit-image.cancel-button" />
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            loading={loading}
            disabled={
              loading || !hasChanges || (canEditName && !imageName.trim())
            }>
            <FormattedMessage id="image-modal.edit-image.save-button" />
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}

EditImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  asset: PropTypes.shape({
    originalName: PropTypes.string,
    savedName: PropTypes.string,
    type: PropTypes.string,
    thumbUrl: PropTypes.string,
    folderId: PropTypes.string,
    createdAt: PropTypes.number,
    metadata: PropTypes.shape({
      format: PropTypes.string,
      size: PropTypes.number,
      width: PropTypes.number,
      height: PropTypes.number,
    }),
  }),
  folders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
};

EditImageModal.defaultProps = {
  asset: null,
  loading: false,
  isLoading: false,
};

export default EditImageModal;
